import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  EmptyState,
  KpiCard,
  PALETTES,
  PageHeader,
  Skeleton,
  formatDate,
  formatMoney,
  formatRelative,
  greeting,
} from '@bixo/ui'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import {
  AlertTriangle,
  ArrowRight,
  Building2,
  CheckCircle2,
  IndianRupee,
  Loader2,
  PauseCircle,
  Plus,
  Receipt,
  Sparkles,
  TrendingUp,
  Wallet,
} from 'lucide-react'
import { StatusBadge } from '../components/status-badge'
import { endpoints, meQuery, qk } from '../lib/api'
import { ck, consoleApi, type ConsoleEvent } from '../lib/console'
import { useCan } from '../lib/access'

const EVENT_ICON: Record<ConsoleEvent['type'], typeof Building2> = {
  signup: Sparkles,
  payment: IndianRupee,
  payment_failed: AlertTriangle,
  suspended: PauseCircle,
}
const EVENT_TONE: Record<ConsoleEvent['type'], string> = {
  signup: 'bg-accent text-accent-foreground',
  payment: 'bg-success-soft text-success',
  payment_failed: 'bg-danger-soft text-danger',
  suspended: 'bg-warning-soft text-warning',
}

function MiniStat({
  label,
  value,
  loading,
  to,
  tone,
  suffix,
}: {
  label: string
  value: number | string | undefined
  loading: boolean
  to?: { tab: 'trials' | 'dunning' }
  tone?: 'success' | 'warning' | 'danger'
  suffix?: string
}) {
  const color =
    tone === 'success'
      ? 'text-success'
      : tone === 'warning'
        ? 'text-warning'
        : tone === 'danger'
          ? 'text-danger'
          : ''
  const body = (
    <>
      <p className="text-xs text-muted-foreground">{label}</p>
      {loading ? (
        <Skeleton className="mt-1 h-6 w-12" />
      ) : (
        <p className={`tabular mt-0.5 text-xl font-semibold ${value && value !== '0 / 0' ? color : ''}`}>
          {value ?? 0}
          {suffix && <span className="text-sm font-normal text-muted-foreground">{suffix}</span>}
        </p>
      )}
    </>
  )
  const cls = 'block rounded-xl border border-border bg-surface px-4 py-3 shadow-soft transition-colors'
  return to ? (
    <Link to="/billing" search={to} className={`${cls} hover:border-foreground/20`}>
      {body}
    </Link>
  ) : (
    <div className={cls}>{body}</div>
  )
}

/** Minimal accessible bar chart: every bar has a title with its exact value. */
function Bars({
  items,
  loading,
}: {
  items: { key: string; label: string; value: number }[]
  loading: boolean
}) {
  if (loading) return <Skeleton className="h-36" />
  const max = Math.max(1, ...items.map((i) => i.value))
  const empty = items.every((i) => i.value === 0)
  return (
    <div className="relative">
      <div
        className="flex h-36 items-end gap-[3px]"
        role="img"
        aria-label={items.map((i) => i.label).join(', ')}
      >
        {items.map((i) => (
          <div
            key={i.key}
            title={i.label}
            className="flex-1 rounded-t-sm bg-brand-500/80 transition-all hover:bg-brand-600"
            style={{ height: `${Math.max(2, (i.value / max) * 100)}%`, opacity: i.value === 0 ? 0.25 : 1 }}
          />
        ))}
      </div>
      {empty && (
        <p className="absolute inset-0 grid place-items-center text-sm text-muted-foreground">No data yet</p>
      )}
    </div>
  )
}

export function OverviewPage() {
  const { data: me } = useSuspenseQuery(meQuery)
  const canCreate = useCan()('tenants.manage')
  const stats = useQuery({
    queryKey: qk.stats,
    queryFn: endpoints.stats,
    refetchInterval: (q) => ((q.state.data?.provisioning ?? 0) > 0 ? 3000 : false),
  })
  const canBilling = useCan()('billing.view')
  const dashboard = useQuery({ queryKey: ck.dashboard, queryFn: consoleApi.dashboard, staleTime: 60_000 })
  const d = dashboard.data
  const change = d?.revenue_change_pct ? Number(d.revenue_change_pct) : null
  const recent = useQuery({
    queryKey: qk.tenants({ page: 1 }),
    queryFn: () => endpoints.tenants({ page: 1 }),
  })

  return (
    <>
      <PageHeader
        eyebrow={greeting() + (me ? `, ${me.name.split(' ')[0] ?? ''}` : '')}
        title="Platform overview"
        description="Every business running on Bixo, at a glance."
        actions={
          canCreate && (
            <Button asChild>
              <Link to="/tenants" search={{ create: true }}>
                <Plus /> Create workspace
              </Link>
            </Button>
          )
        }
      />

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" aria-label="Workspace counts">
        <KpiCard
          index={0}
          label="Workspaces"
          value={stats.data?.total ?? 0}
          icon={Building2}
          loading={stats.isPending}
          hint="All time"
        />
        <KpiCard
          index={1}
          label="Active"
          value={stats.data?.active ?? 0}
          icon={CheckCircle2}
          loading={stats.isPending}
          hint="Serving customers"
        />
        <KpiCard
          index={2}
          label="Provisioning"
          value={stats.data?.provisioning ?? 0}
          icon={Loader2}
          loading={stats.isPending}
          hint={stats.data?.failed ? `${stats.data.failed} failed` : 'Databases being created'}
        />
        <KpiCard
          index={3}
          label="Suspended"
          value={stats.data?.suspended ?? 0}
          icon={PauseCircle}
          loading={stats.isPending}
          hint="Access paused"
        />
      </section>

      <section className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-4" aria-label="Revenue">
        <KpiCard
          index={4}
          label="MRR"
          value={`${formatMoney(d?.mrr ?? '0')}`}
          icon={IndianRupee}
          loading={dashboard.isPending}
          hint={`${d?.paying ?? 0} paying · ARPA ${formatMoney(d?.arpa ?? '0')}`}
        />
        <KpiCard
          index={5}
          label="ARR"
          value={`${formatMoney(d?.arr ?? '0')}`}
          icon={TrendingUp}
          loading={dashboard.isPending}
          hint="MRR × 12, excl. GST"
        />
        <KpiCard
          index={6}
          label="Revenue this month"
          value={`${formatMoney(d?.revenue_this_month ?? '0')}`}
          icon={Wallet}
          loading={dashboard.isPending}
          hint={
            change === null
              ? `Last month ${formatMoney(d?.revenue_last_month ?? '0')}`
              : `${change >= 0 ? '▲' : '▼'} ${Math.abs(change)}% vs last month`
          }
        />
        <KpiCard
          index={7}
          label="Outstanding"
          value={`${formatMoney(d?.outstanding ?? '0')}`}
          icon={Receipt}
          loading={dashboard.isPending}
          hint={
            d?.overdue_invoices
              ? `${d.overdue_invoices} invoice${d.overdue_invoices === 1 ? '' : 's'} overdue`
              : 'Unpaid invoices'
          }
        />
      </section>

      <section className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-5" aria-label="Funnel">
        <MiniStat
          label="On trial"
          value={d?.trials.active}
          loading={dashboard.isPending}
          to={{ tab: 'trials' }}
        />
        <MiniStat
          label="Trials ending ≤ 7d"
          value={d?.trials.ending_7d}
          loading={dashboard.isPending}
          to={{ tab: 'trials' }}
          tone="warning"
        />
        <MiniStat
          label="Converted this month"
          value={d?.conversions_this_month}
          loading={dashboard.isPending}
          tone="success"
        />
        <MiniStat
          label="Churned this month"
          value={d?.churned_this_month}
          loading={dashboard.isPending}
          suffix={d ? ` · ${d.churn_rate_pct}%` : ''}
          tone="danger"
        />
        <MiniStat
          label="In grace / failed (30d)"
          value={d ? `${d.in_grace} / ${d.failed_payments_30d}` : undefined}
          loading={dashboard.isPending}
          to={{ tab: 'dunning' }}
          tone="danger"
        />
      </section>

      <section className="mt-6 grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Signups</CardTitle>
            <CardDescription>New workspaces per day, last 30 days.</CardDescription>
          </CardHeader>
          <CardContent>
            <Bars
              loading={dashboard.isPending}
              items={(d?.signups_30d ?? []).map((day) => ({
                key: day.date,
                label: `${formatDate(day.date)}: ${day.count}`,
                value: day.count,
              }))}
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Revenue</CardTitle>
            <CardDescription>
              Paid per month (taxable value, less credit notes), last 12 months.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Bars
              loading={dashboard.isPending}
              items={(d?.revenue_12m ?? []).map((m) => ({
                key: m.month,
                label: `${m.month}: ${formatMoney(m.total)}`,
                value: Number(m.total),
              }))}
            />
          </CardContent>
        </Card>
      </section>

      <section className="mt-4 grid gap-4 lg:grid-cols-[1fr_1.4fr]">
        <Card>
          <CardHeader>
            <CardTitle>Plan mix</CardTitle>
            <CardDescription>Live and paused workspaces by plan.</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3">
            {dashboard.isPending ? (
              <Skeleton className="h-32" />
            ) : (
              (d?.plan_mix ?? []).map((p) => {
                const total = Math.max(
                  1,
                  (d?.plan_mix ?? []).reduce((sum, x) => sum + x.count, 0),
                )
                return (
                  <div key={p.plan} className="grid gap-1">
                    <div className="flex justify-between text-sm">
                      <span>{p.name}</span>
                      <span className="tabular text-muted-foreground">{p.count}</span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full rounded-full bg-brand-500 transition-[width] duration-700"
                        style={{ width: `${(p.count / total) * 100}%` }}
                      />
                    </div>
                  </div>
                )
              })
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex-row items-center justify-between">
            <div>
              <CardTitle>Activity</CardTitle>
              <CardDescription>Sign-ups, payments, failures and pauses.</CardDescription>
            </div>
            {canBilling && (
              <Button asChild variant="ghost" size="sm">
                <Link to="/billing" search={{ tab: 'payments' }}>
                  Billing <ArrowRight />
                </Link>
              </Button>
            )}
          </CardHeader>
          <CardContent className="pt-0">
            {dashboard.isPending ? (
              <Skeleton className="h-48" />
            ) : !d?.events.length ? (
              <p className="py-6 text-center text-sm text-muted-foreground">Nothing has happened yet.</p>
            ) : (
              <ul className="divide-y divide-border">
                {d.events.slice(0, 10).map((e, i) => {
                  const Icon = EVENT_ICON[e.type]
                  return (
                    <li key={`${e.type}-${e.at}-${i}`} className="flex items-center gap-3 py-2.5">
                      <span
                        className={`grid size-8 shrink-0 place-items-center rounded-lg ${EVENT_TONE[e.type]}`}
                      >
                        <Icon className="size-4" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm">
                          <span className="font-medium">{e.title}</span>
                          {e.tenant && (
                            <>
                              {' · '}
                              <Link
                                to="/tenants/$tenantId"
                                params={{ tenantId: e.tenant.id }}
                                className="hover:underline"
                              >
                                {e.tenant.name}
                              </Link>
                            </>
                          )}
                        </span>
                        <span className="block truncate text-xs text-muted-foreground capitalize">
                          {e.detail ?? ''}
                          {e.detail ? ' · ' : ''}
                          {formatRelative(e.at)}
                        </span>
                      </span>
                      {e.amount && (
                        <span className="tabular text-sm font-semibold text-success">
                          {formatMoney(e.amount)}
                        </span>
                      )}
                    </li>
                  )
                })}
              </ul>
            )}
          </CardContent>
        </Card>
      </section>

      {(stats.data?.failed ?? 0) > 0 && (
        <div className="mt-4 flex items-center gap-3 rounded-xl border border-danger/25 bg-danger-soft px-4 py-3 text-sm text-danger">
          <AlertTriangle className="size-4 shrink-0" />
          {stats.data?.failed} workspace{stats.data?.failed === 1 ? '' : 's'} failed to provision.
          <Link to="/tenants" search={{ status: 'failed' }} className="ml-auto font-medium hover:underline">
            Review
          </Link>
        </div>
      )}

      <Card className="mt-6">
        <CardHeader className="flex-row items-center justify-between">
          <div>
            <CardTitle>Recently created</CardTitle>
            <CardDescription>The newest workspaces on the platform.</CardDescription>
          </div>
          <Button asChild variant="ghost" size="sm">
            <Link to="/tenants">
              View all <ArrowRight />
            </Link>
          </Button>
        </CardHeader>
        <CardContent className="pt-3">
          {recent.isPending ? (
            <div className="grid gap-3">
              {Array.from({ length: 4 }, (_, i) => (
                <Skeleton key={i} className="h-12" />
              ))}
            </div>
          ) : recent.data && recent.data.data.length > 0 ? (
            <ul className="divide-y divide-border">
              {recent.data.data.slice(0, 6).map((tenant) => (
                <li key={tenant.id}>
                  <Link
                    to="/tenants/$tenantId"
                    params={{ tenantId: tenant.id }}
                    className="-mx-2 flex items-center gap-3 rounded-lg px-2 py-3 transition-colors hover:bg-surface-2"
                  >
                    <span
                      className="grid size-9 shrink-0 place-items-center rounded-lg text-sm font-semibold text-white"
                      style={{ background: PALETTES[tenant.palette].ramp[600] }}
                    >
                      {tenant.name.charAt(0).toUpperCase()}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{tenant.name}</span>
                      <span className="block truncate text-xs text-muted-foreground">{tenant.domain}</span>
                    </span>
                    <span className="hidden text-xs text-muted-foreground sm:block">
                      {formatRelative(tenant.created_at)}
                    </span>
                    <StatusBadge status={tenant.status} />
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyState
              icon={Building2}
              title="No workspaces yet"
              description="Create the first business workspace to get started."
              action={
                canCreate && (
                  <Button asChild>
                    <Link to="/tenants" search={{ create: true }}>
                      <Plus /> Create workspace
                    </Link>
                  </Button>
                )
              }
            />
          )}
        </CardContent>
      </Card>
    </>
  )
}
